import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  DatePicker,
  Button,
  Space,
  Typography,
  message,
  Statistic,
  Row,
  Col,
  Tag,
  Spin
} from 'antd';
import {
  UserOutlined,
  ReloadOutlined,
  CalendarOutlined,
  TeamOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { referralService } from '../../services';
import { formatNumber } from '../../utils/helpers';

const { Title, Text } = Typography;
const { MonthPicker } = DatePicker;

const ReferralList = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({});
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [selectedMonth, setSelectedMonth] = useState(dayjs());

  useEffect(() => {
    loadData();
  }, [selectedMonth, pagination.current, pagination.pageSize]);

  const loadData = async () => {
    setLoading(true);
    try {
      const thangnam = selectedMonth.format('YYYY-MM-01');
      const response = await referralService.getMonthlyReferrals(
        thangnam,
        pagination.current,
        pagination.pageSize
      );

      if (response.success) {
        setData(response.data.items || []);
        setSummary(response.data.summary || {});
        setPagination(prev => ({
          ...prev,
          total: response.data.summary?.total_records || 0
        }));
      } else {
        message.error(response.message || 'Không thể tải dữ liệu');
      }
    } catch (error) {
      message.error('Lỗi khi tải dữ liệu: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (paginationInfo) => {
    setPagination(prev => ({
      ...prev,
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize
    }));
  };

  const columns = [
    {
      title: 'STT',
      key: 'index',
      width: 60,
      render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1
    },
    {
      title: 'Họ tên',
      dataIndex: 'full_name',
      key: 'full_name',
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: 'Ngày sinh',
      dataIndex: 'NgaySinh',
      key: 'NgaySinh',
      render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : '-'
    },
    {
      title: 'Giới tính',
      dataIndex: 'GioiTinh',
      key: 'GioiTinh',
      render: (gender) => (
        <Tag color={gender === 'Nam' ? 'blue' : 'pink'}>
          {gender}
        </Tag>
      )
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email'
    },
    {
      title: 'Số điện thoại',
      dataIndex: 'SDT',
      key: 'SDT'
    },
    {
      title: 'Số người giới thiệu',
      dataIndex: 'SoNguoi',
      key: 'SoNguoi',
      render: (count) => (
        <Tag color={count >= 10 ? 'green' : count >= 5 ? 'orange' : 'default'}>
          {formatNumber(count)} người
        </Tag>
      ),
      sorter: true
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ marginBottom: 24 }}>
          <Title level={3}>
            <TeamOutlined style={{ marginRight: 8 }} />
            Danh sách người giới thiệu
          </Title>
          <Text type="secondary">
            Quản lý danh sách người dùng có giới thiệu trong tháng
          </Text>
        </div>

        {/* Thống kê tổng quan */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title="Tổng số người có giới thiệu"
                value={summary.total_records || 0}
                prefix={<UserOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title="Tổng số người được giới thiệu"
                value={summary.total_referrals_count || 0}
                prefix={<TeamOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title="Người đạt ≥ 10 giới thiệu"
                value={summary.total_records_at_least_10 || 0}
                prefix={<UserOutlined />}
                valueStyle={{ color: '#fa8c16' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Bộ lọc */}
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <CalendarOutlined />
            <Text>Chọn tháng:</Text>
            <MonthPicker
              value={selectedMonth}
              onChange={setSelectedMonth}
              format="MM/YYYY"
              placeholder="Chọn tháng"
            />
          </Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={loadData}
            loading={loading}
          >
            Làm mới
          </Button>
        </div>

        {/* Bảng dữ liệu */}
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} của ${total} bản ghi`
          }}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
        />
      </Card>
    </div>
  );
};

export default ReferralList;